import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { push } from "connected-react-router";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import getWhitelabelledLogo from "modules/company/utils/getWhitelabelledLogo";
import { setAppLogo } from "modules/appSetting";
import { setHasTickets } from "modules/ticketing/redux/slice";
import { selectUser } from "modules/auth/redux/selectors";
import { getEventTickets } from "modules/eventForm/ticketing/apis";
import logger from "logging/logger";
import { useLocation } from "react-router";
import {
  addEventDetails,
  setCurrentEventCompany,
  setCurrentEventCompanyPlan,
  setCurrentEventId,
  setEventMember,
  setLoadingEvent,
} from "modules/event/eventSlice";
import {
  attendEvent,
  getEventByCode,
  getEventMemberStatus,
} from "services/apiService/apis";
import {
  selectCurrentEvent,
  selectCurrentEventCompanyPlan,
  selectEventMember,
  selectIsEventManager,
} from "modules/event/selectors";
import useCompany from "modules/company/hooks/useCompany";
import useRegisterToEvent from "modules/event/hooks/useRegisterToEvent";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";

export const EventLoader = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const eventCode = useMemo(() => {
    const regex = /\/e\/([^/?#]+)/i;
    const match = pathname.match(regex);
    if (match && match[1]) {
      return match[1];
    }
    return null;
  }, [pathname]);

  const event = useSelector(selectCurrentEvent);
  const eventMember = useSelector(selectEventMember);
  const user = useSelector(selectUser);
  const { company, getCompanyPlanData } = useCompany();
  const { addErrorNotification } = useNotificationActions();
  const eventCompanyPlan = useSelector(selectCurrentEventCompanyPlan);
  const hasEventManagerAccess = useSelector(selectIsEventManager);
  const { getEventMemberData, registerToEvent } = useRegisterToEvent();
  const { t } = useI18n(["common", "event", "server"]);
  const { checkIsAllowedToJoinEvent } = useCheckEventStatus();

  useEffect(
    () => () => {
      if (!company) {
        dispatch(setAppLogo(null));

        return;
      }
      const whitelabelLogo = getWhitelabelledLogo(company);

      if (whitelabelLogo) {
        dispatch(setAppLogo(whitelabelLogo));
      } else {
        dispatch(setAppLogo(null));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (event && hasEventManagerAccess && !eventMember) {
      registerToEvent(event?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, hasEventManagerAccess, eventMember]);

  // Get event details
  // Get company plan
  // Get event member
  const loadEventData = async () => {
    dispatch(setLoadingEvent(true));

    if (!eventCode) {
      dispatch(setLoadingEvent(false));

      return;
    }
    const data = await getEventByCode(eventCode);

    if (data && data.event) {
      dispatch(addEventDetails(data.event));
      dispatch(setCurrentEventId(data.event.id));

      if (data.event.eventcubeEventId) {
        const result = await getEventTickets(data.event.id);

        if (result.message) {
          logger.error(
            `[Ticketing][EventViewContext] Error getting tickets: ${result.message}`,
          );

          dispatch(setHasTickets(false));
        } else {
          dispatch(setHasTickets(result.tickets.length > 0));
        }
      }

      if (data.event.company && typeof data.event.company !== "string") {
        dispatch(setAppLogo(getWhitelabelledLogo(data.event.company)));
        dispatch(setCurrentEventCompany(data.event.company));
      }

      if (!user && typeof data.event.company !== "string") {
        const plan = await getCompanyPlanData(data.event.company);
        dispatch(setCurrentEventCompanyPlan(plan));
      }

      if (user && typeof data.event.company !== "string") {
        // eslint-disable-next-line promise/catch-or-return
        Promise.all([
          getCompanyPlanData(data.event.company),
          getEventMemberStatus(data.event.id),

          // eslint-disable-next-line promise/prefer-await-to-then
        ]).then(([plan, { data: member }]) => {
          if (!data || !data.event) {
            return;
          }

          dispatch(setCurrentEventCompanyPlan(plan));

          // eslint-disable-next-line promise/always-return
          if (
            member &&
            member.status !== "attended" &&
            checkIsAllowedToJoinEvent(data.event)
          ) {
            attendEvent(data.event.id)
              // eslint-disable-next-line promise/prefer-await-to-then
              .then(({ member: updatedMember, message = "" }) => {
                // eslint-disable-next-line promise/always-return
                if (updatedMember) {
                  dispatch(setEventMember(updatedMember));
                } else {
                  logger.error(
                    `[EventLoader] user not able to attend event ${data?.event?.id} message: ${message}`,
                  );
                  addErrorNotification({
                    message: t(message) || t("try.again.later"),
                  });
                  dispatch(push(`/e/${data?.event?.code}/register`));
                }
              })
              // eslint-disable-next-line promise/prefer-await-to-then
              .catch((err) => {
                logger.error(
                  `[EventLoader] user not able to attend event ${data?.event?.id}`,
                  err,
                );
                addErrorNotification({
                  message: err ? t(err.message) : t("try.again.later"),
                });
                dispatch(push(`/e/${data?.event?.code}/register`));
              });
          }
        });
      }
    }
    dispatch(setLoadingEvent(false));
  };

  useEffect(() => {
    if (user && event?.id && !eventMember) {
      // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
      getEventMemberData(event?.id).then((eventMemberData) => {
        // eslint-disable-next-line promise/always-return
        if (eventMemberData) {
          dispatch(setEventMember(eventMemberData));
        }
      });
    } else if (!user || !event?.id) {
      /**
       * Only set the event member to null if we either don't have user or event data.
       * Without the if condition in the else block, we saw that when the user tried to
       * register for an event, the "Save me a spot" button would disable and enable again
       * even if the registration was successful. That's because if we have user, eventData
       * and eventMember, then we would execute this else block. Putting a condition in this
       * else block solves this problem.
       */
      dispatch(setEventMember(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, event?.id]);

  useEffect(() => {
    if (eventCode && (!event || !eventCompanyPlan)) {
      loadEventData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventCode, event]);

  useEffect(
    () => () => {
      dispatch(setCurrentEventCompany(null));
      dispatch(setCurrentEventCompanyPlan(null));
    },
    [dispatch],
  );

  return null;
};
